import {Component, computed, DestroyRef, effect, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {DemonstrationAgenda, DirectusService} from '@frontend/directus';
import {
  DemonstrationAgendaItemRequest,
  DemonstrationAgendaItemResponse,
  DemonstrationAgendaUpsertRequest,
  DemonstrationControllerService,
  ProjectChapterType,
  ProjectStepType,
} from '@frontend/shared-api';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {ProjectStepSaveButtonComponent} from '../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {CardModule} from 'primeng/card';
import {AgendaItem} from '../components/demonstration.model';
import {DemonstrationAgendaTableComponent} from '../components/demonstration-agenda-table/demonstration-agenda-table.component';
import {NoDataComponent} from '../../../../../../shared/components/no-data/no-data.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {TranslocoDirective} from '@jsverse/transloco';
import {ConfirmationDialogService} from '../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-step-9-1-demonstration-agenda',
  imports: [
    FormsModule,
    ProjectStepSaveButtonComponent,
    CardModule,
    DemonstrationAgendaTableComponent,
    NoDataComponent,
    LoadingComponent,
    TranslocoDirective,
  ],
  templateUrl: './step-7-1-demonstration-agenda.component.html',
})
export class Step71DemonstrationAgendaComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly directusService = inject(DirectusService);
  private readonly agendaService = inject(DemonstrationControllerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly directusAgendaItems = signal<DemonstrationAgenda[]>([]);
  protected readonly savedAgenda = signal<DemonstrationAgendaItemResponse[]>([]);
  protected readonly agendaItems = signal<AgendaItem[]>([]);
  protected readonly expandedAgendaItemIds = signal<Set<number>>(new Set());

  protected readonly hasChanges = computed(() => {
    const current = this.agendaItems();
    const saved = this.savedAgenda();

    if (current.length !== saved.length) return true;

    return current.some((item) => {
      const savedItem = saved.find(
        (s) => s.demonstrationAgendaDirectusId === item.demonstrationAgendaDirectusId,
      );
      if (!savedItem) return true;

      return savedItem.duration !== item.duration || savedItem.orderNumber !== item.orderNumber;
    });
  });

  protected readonly isRfpStepCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFP, stepId: ProjectStepType.RFP_PROVIDER_RESULTS},
    ]);
  });

  protected readonly shouldShowContent = computed(() => {
    return !this.loading() && this.isRfpStepCompleted();
  });

  protected readonly prerequisiteInfo = computed(
    (): {icon: string; titleKey: string; messageKey: string} | null => {
      if (this.loading()) {
        return null;
      }

      if (!this.isRfpStepCompleted()) {
        return {
          icon: 'pi pi-clock text-4xl text-gray-400 mb-4',
          titleKey: 'project.steps.previousStepIncompleteTitle',
          messageKey: 'project.steps.previousStepIncompleteMessage',
        };
      }

      return null;
    },
  );

  constructor() {
    super();

    effect(() => {
      const directusItems = this.directusAgendaItems();
      const savedItems = this.savedAgenda();

      if (directusItems.length === 0) return;

      const items = this.mergeDirectusAndSavedData(directusItems, savedItems);
      this.agendaItems.set(items);
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.loadData();
  }

  private loadData() {
    this.loading.set(true);

    this.directusService
      .loadDemonstrationAgenda()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (directusData) => {
          this.directusAgendaItems.set(directusData);
          this.loadSavedAgenda();
        },
        error: (error: unknown) => {
          console.error('Error loading system demonstration agenda from Directus:', error);
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  private loadSavedAgenda() {
    this.agendaService
      .getAgenda({projectId: this.projectId})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedData: DemonstrationAgendaItemResponse[]) => {
          this.savedAgenda.set(savedData);
        },
        error: () => {
          this.savedAgenda.set([]);
        },
      });
  }

  private mergeDirectusAndSavedData(
    directusItems: DemonstrationAgenda[],
    savedItems: DemonstrationAgendaItemResponse[],
  ): AgendaItem[] {
    // If we have saved items, the list is fixed. We only show what is saved.
    if (savedItems.length > 0) {
      const items: AgendaItem[] = [];
      savedItems.forEach((savedItem) => {
        const directusItem = directusItems.find(
          (d) => d.id === savedItem.demonstrationAgendaDirectusId,
        );

        // Only include if we can find the corresponding directus item (for translations)
        if (directusItem) {
          items.push({
            demonstrationAgendaDirectusId: savedItem.demonstrationAgendaDirectusId ?? 0,
            translations: directusItem.translations,
            duration: savedItem.duration ?? 0,
            orderNumber: savedItem.orderNumber ?? 0,
            defaultSortOrder: directusItem.default_sort_order,
            defaultDuration: directusItem.default_duration,
          });
        }
      });
      return items.sort((a, b) => a.orderNumber - b.orderNumber);
    }

    // Initial state: Use all items from Directus
    const items: AgendaItem[] = directusItems.map((directusItem) => {
      return {
        demonstrationAgendaDirectusId: directusItem.id,
        translations: directusItem.translations,
        duration: directusItem.default_duration,
        orderNumber: directusItem.default_sort_order,
        defaultSortOrder: directusItem.default_sort_order,
        defaultDuration: directusItem.default_duration,
      };
    });

    // Sort by default sort order initially
    return items.sort((a, b) => a.orderNumber - b.orderNumber);
  }

  protected toggleAgendaItemExpansion(itemId: number) {
    this.expandedAgendaItemIds.update((current) => {
      const next = new Set(current);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }

  protected onDurationChange(item: AgendaItem, newDuration: number) {
    const items = this.agendaItems();
    const index = items.findIndex(
      (i) => i.demonstrationAgendaDirectusId === item.demonstrationAgendaDirectusId,
    );

    if (index !== -1) {
      // Check if duration is actually changing
      if (item.duration === newDuration) {
        return;
      }

      // Check if this agenda item already has saved use cases that have been started
      const savedItem = this.savedAgenda().find(
        (s) => s.demonstrationAgendaDirectusId === item.demonstrationAgendaDirectusId,
      );

      if (savedItem) {
        // Show confirmation dialog
        this.confirmDialogService.confirm({
          headerKey: 'project.steps.rfd.agenda.durationChangeWarningTitle',
          messageKey: 'project.steps.rfd.agenda.durationChangeWarningMessage',
          accept: () => {
            const updatedItems = [...items];
            updatedItems[index] = {...updatedItems[index], duration: newDuration};
            this.agendaItems.set(updatedItems);
          },
          reject: () => {
            // Force component update
            this.agendaItems.set([...items]);
          },
        });
      } else {
        // No saved item yet, just update
        const updatedItems = [...items];
        updatedItems[index] = {...updatedItems[index], duration: newDuration};
        this.agendaItems.set(updatedItems);
      }
    }
  }

  protected moveAgendaItemUp(index: number) {
    if (index === 0) return;

    const items = [...this.agendaItems()];
    const temp = items[index];
    items[index] = items[index - 1];
    items[index - 1] = temp;

    // Update order numbers
    items.forEach((item, idx) => {
      item.orderNumber = idx + 1;
    });

    this.agendaItems.set(items);
  }

  protected moveAgendaItemDown(index: number) {
    const items = this.agendaItems();
    if (index === items.length - 1) return;

    const updatedItems = [...items];
    const temp = updatedItems[index];
    updatedItems[index] = updatedItems[index + 1];
    updatedItems[index + 1] = temp;

    // Update order numbers
    updatedItems.forEach((item, idx) => {
      item.orderNumber = idx + 1;
    });

    this.agendaItems.set(updatedItems);
  }

  protected onSave() {
    if (this.saving() || !this.hasChanges()) return;

    this.saving.set(true);

    const request: DemonstrationAgendaUpsertRequest = {
      items: this.agendaItems().map((item): DemonstrationAgendaItemRequest => ({
        demonstrationAgendaDirectusId: item.demonstrationAgendaDirectusId,
        orderNumber: item.orderNumber,
        duration: item.duration,
      })),
    };

    this.agendaService
      .upsertAgenda({
        projectId: this.projectId,
        demonstrationAgendaUpsertRequest: request,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: async (response: DemonstrationAgendaItemResponse[]) => {
          this.savedAgenda.set(response);
          await this.stepState.loadStepStatuses(this.projectId);
          await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
        },
        error: (error: unknown) => {
          console.error('Error saving agenda:', error);
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }
}
